import { useMemo, useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";

// formik components
import { useFormikContext } from "formik";

// toast notifications
import { toast } from "react-toastify";

// Material Dashboard 2 PRO React TS components
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";

import { CountryExportOption } from "../../../schemas/exportSchema/form";
import {
  EXPORT_DURATION_WARN_THRESHOLD,
  MAX_COUNTRIES_EXPORT,
} from "../../../schemas/exportSchema/limits";
import {
  filterOptionsForDimension,
  getGroupFieldValue,
  GROUP_DIMENSION_LABELS,
  GroupDimension,
  groupSelectionState,
  toggleGroup,
} from "../utils/groupSelection";

type GroupedOption = CountryExportOption & { groupKey: string };

function ExportCountriesForm({ formData }: any): JSX.Element {
  const { formField, errors, touched, values } = formData;
  const { countryExport } = formField;
  const { setFieldValue, setFieldTouched } = useFormikContext<any>();

  // State for the current grouping dimension (continent, idfRegion, whoRegion)
  const [dimension, setDimension] = useState<GroupDimension>("continent");

  // Get the currently selected location codes from the form values
  const selectedLocCodes: number[] = values[countryExport.name] ?? [];

  // Memoized list of all country options
  const allOptions = useMemo(
    () => (countryExport?.options ?? []) as CountryExportOption[],
    [countryExport?.options]
  );

  // Memoized list of grouped options based on the current dimension
  const groupedOptions: GroupedOption[] = useMemo(() => {
    return filterOptionsForDimension(allOptions, dimension)
      .map((option) => {
        const groupKey = getGroupFieldValue(option, dimension);
        if (groupKey == null) return null;
        return { ...option, groupKey };
      })
      .filter((option): option is GroupedOption => option != null)
      .sort((a, b) => {
        const byGroup = a.groupKey.localeCompare(b.groupKey);
        if (byGroup !== 0) return byGroup;
        return a.label.localeCompare(b.label);
      });
  }, [allOptions, dimension]);

  // Memoized list of selected options based on the selected location codes
  const selectedOptions = useMemo(() => {
    const byValue = new Map(allOptions.map((option) => [option.value, option]));
    return selectedLocCodes
      .map((code) => byValue.get(code))
      .filter((option): option is CountryExportOption => option != null);
  }, [allOptions, selectedLocCodes]);

  // Memoized map of group keys to their corresponding options for quick access
  const optionsByGroup = useMemo(() => {
    const map = new Map<string, GroupedOption[]>();
    for (const option of groupedOptions) {
      const list = map.get(option.groupKey) ?? [];
      list.push(option);
      map.set(option.groupKey, list);
    }
    return map;
  }, [groupedOptions]);

  // Function to apply the selected location codes to the form values
  const applyLocCodes = (next: number[]) => {
    setFieldValue(countryExport.name, next);
    setFieldTouched(countryExport.name, true);
  };

  // Function to handle toggling the selection of an entire group of countries
  const handleToggleGroup = (groupKey: string) => {
    const groupOptions = optionsByGroup.get(groupKey) ?? [];
    const groupLocCodes = groupOptions.map((option) => option.value);
    const result = toggleGroup(selectedLocCodes, groupLocCodes, MAX_COUNTRIES_EXPORT);

    applyLocCodes(result.nextSelected);

    if (!result.removed && result.skippedCount > 0) {
      toast.info(
        `Group "${groupKey}" has ${groupLocCodes.length} countries; only ${result.addedCount} added (max ${MAX_COUNTRIES_EXPORT}).`
      );
    }
  };

  return (
    <MDBox>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <MDTypography variant="caption" fontWeight="regular" sx={{ display: "block", mb: 0.5 }}>
            Group by
          </MDTypography>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={dimension}
            onChange={(_, next: GroupDimension | null) => {
              if (next != null) setDimension(next);
            }}
            aria-label="Group countries by"
          >
            {(Object.keys(GROUP_DIMENSION_LABELS) as GroupDimension[]).map((key) => (
              <ToggleButton key={key} value={key}>
                {GROUP_DIMENSION_LABELS[key]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Grid>

        <Grid item xs={12}>
          <Autocomplete<GroupedOption, true, false, false>
            multiple
            disableCloseOnSelect
            options={groupedOptions}
            value={selectedOptions as GroupedOption[]}
            groupBy={(option) => option.groupKey}
            isOptionEqualToValue={(option, value) => value?.value === option?.value}
            getOptionLabel={(option) => option?.label || ""}
            getOptionDisabled={(option) =>
              selectedLocCodes.length >= MAX_COUNTRIES_EXPORT &&
              !selectedLocCodes.includes(option.value)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label={countryExport.label}
                helperText={`${selectedLocCodes.length} / ${MAX_COUNTRIES_EXPORT} selected`}
              />
            )}
            renderGroup={(params) => {
              const groupOptions = optionsByGroup.get(params.group) ?? [];
              const groupLocCodes = groupOptions.map((option) => option.value);
              const state = groupSelectionState(selectedLocCodes, groupLocCodes);
              const SelectionIcon =
                state === "checked"
                  ? CheckBoxIcon
                  : state === "indeterminate"
                  ? IndeterminateCheckBoxIcon
                  : CheckBoxOutlineBlankIcon;

              return (
                <li key={params.key}>
                  <MDBox
                    component="div"
                    role="button"
                    tabIndex={0}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      handleToggleGroup(params.group);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleToggleGroup(params.group);
                      }
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      px: 1.5,
                      py: 0.75,
                      cursor: "pointer",
                      position: "sticky",
                      top: -8,
                      zIndex: 1,
                      bgcolor: "background.paper",
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <SelectionIcon
                      fontSize="small"
                      style={{ marginRight: 8, padding: 9, boxSizing: "content-box" }}
                    />
                    <MDTypography variant="button" fontWeight="medium">
                      {params.group}
                    </MDTypography>
                    <MDTypography variant="caption" sx={{ ml: 1 }} color="text">
                      ({groupLocCodes.length})
                    </MDTypography>
                  </MDBox>
                  <ul style={{ padding: 0 }}>{params.children}</ul>
                </li>
              );
            }}
            renderOption={(props, option, { selected }) => {
              const SelectionIcon = selected ? CheckBoxIcon : CheckBoxOutlineBlankIcon;

              return (
                <li {...props} key={option.value}>
                  <SelectionIcon
                    fontSize="small"
                    style={{ marginRight: 8, padding: 9, boxSizing: "content-box" }}
                  />
                  {option.label}
                </li>
              );
            }}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option.value} label={option.label} />
              ))
            }
            onChange={(_, selected) => {
              if (selected !== null) {
                applyLocCodes(selected.map((s) => s.value));
              }
            }}
          />
        </Grid>

        {selectedLocCodes.length > EXPORT_DURATION_WARN_THRESHOLD && (
          <Grid item xs={12}>
            <MDTypography variant="caption" color="warning" fontWeight="regular">
              Exporting more than {EXPORT_DURATION_WARN_THRESHOLD} countries may take longer.
            </MDTypography>
          </Grid>
        )}

        <Grid item xs={12}>
          <MDTypography
            variant="caption"
            color="error"
            fontWeight="regular"
            sx={{ whiteSpace: "pre-wrap" }}
          >
            {errors[countryExport.name] && touched[countryExport.name]
              ? errors[countryExport.name]
              : ""}
          </MDTypography>
        </Grid>
      </Grid>
    </MDBox>
  );
}

export default ExportCountriesForm;
